import { useState } from 'react';
import { useRoute, Link } from 'wouter';
import { trpc } from '../../lib/trpc';
import { useAsync } from '../../lib/useAsync';
import { Loading, ErrorBox, StatusPill, PageTitle, Toggle } from '../../components/admin/ui';
import {
  formatInt,
  PLANO_LABEL,
  ASSINATURA_LABEL,
  AGENTE_STATUS_LABEL,
  AGENTE_STATUS_COLOR,
} from '../../lib/format';

const TIPO_LABEL: Record<string, string> = {
  ingestao: 'Ingestão',
  risco: 'Risco',
  comunicacao: 'Comunicação',
};

function AcaoSuporte({ label, badge, onClick }: { label: string; badge?: number; onClick: () => void }) {
  return (
    <button
      className="flex w-full items-center justify-between rounded-sm border border-line-2 px-4 py-3 text-left text-sm text-ink transition-colors hover:bg-surface-2"
      onClick={onClick}
    >
      <span>{label}</span>
      {badge != null && (
        <span
          className="rounded px-1.5 py-0.5 text-[11px] font-semibold"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent-bright)' }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

interface ResumoVarredura {
  contratos: number;
  contratosComFlag: number;
  flags: number;
  comunicacoes: number;
}

/**
 * Modo demonstração (14 dias) — override manual por org + varredura sob
 * demanda. A regra padrão (comunicações nascem "simuladas" nos primeiros 14
 * dias de cada conta) continua intacta para toda org sem override; isto é só
 * um escape hatch de super-admin para testar envio real numa org específica
 * sem esperar. Ver apps/api/src/comm/regras.ts (statusInicial).
 */
function ModoDemonstracao({
  orgId,
  override,
  onChangeOverride,
}: {
  orgId: string;
  override: boolean | null;
  onChangeOverride: () => void;
}) {
  const [salvando, setSalvando] = useState(false);
  const [rodando, setRodando] = useState(false);
  const [resumo, setResumo] = useState<ResumoVarredura | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const foraDaDemo = override === false;

  async function alternar() {
    setSalvando(true);
    setErro(null);
    try {
      // Liga → força sair (false). Desliga → volta pro padrão (null), nunca
      // força "entrar" em demonstração por aqui (isso não tem controle de UI
      // hoje — o campo suporta `true`, mas não é um caso de uso pedido).
      await trpc.admin.demonstracao.setOverride.mutate({ orgId, override: foraDaDemo ? null : false });
      onChangeOverride();
    } catch (err) {
      setErro((err as Error).message ?? 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  async function rodarAgora() {
    setRodando(true);
    setErro(null);
    setResumo(null);
    try {
      const r = await trpc.admin.demonstracao.rodarVarredura.mutate({ orgId });
      setResumo(r);
    } catch (err) {
      setErro((err as Error).message ?? 'Não foi possível rodar a varredura.');
    } finally {
      setRodando(false);
    }
  }

  return (
    <div className="card p-5" data-testid="card-modo-demonstracao">
      <div className="text-[11px] uppercase tracking-wider text-ink-4">Modo demonstração</div>
      <p className="mt-2 text-xs text-ink-3">
        Nos primeiros 14 dias de cada conta, as comunicações nascem "simuladas" (nunca são enviadas de verdade). Use
        o controle abaixo só pra testar o envio real hoje, sem esperar — afeta SÓ esta org.
      </p>

      <div className="mt-4 flex items-center justify-between gap-3 rounded-sm border border-line-2 px-4 py-3">
        <div>
          <div className="text-sm text-ink">Enviar comunicações reais</div>
          <div className="mt-0.5 text-xs text-ink-4">
            {foraDaDemo
              ? 'Fora do modo demonstração — comunicações novas já nascem reais.'
              : 'Regra padrão: modo demonstração nos primeiros 14 dias.'}
          </div>
        </div>
        <Toggle
          ligado={foraDaDemo}
          onChange={alternar}
          disabled={salvando}
          label="Enviar comunicações reais (sair do modo demonstração)"
        />
      </div>

      <button
        type="button"
        onClick={rodarAgora}
        disabled={rodando}
        className="btn btn-ghost mt-3 w-full justify-center disabled:opacity-60"
        data-testid="btn-rodar-varredura"
      >
        {rodando ? 'Rodando varredura…' : 'Rodar varredura agora'}
      </button>

      {resumo && (
        <div
          className="mt-3 rounded-sm px-3 py-2 text-xs"
          style={{ background: 'var(--surface-2)', color: 'var(--ink-3)' }}
          data-testid="resumo-varredura"
        >
          Varredura concluída: {resumo.contratos} contratos · {resumo.contratosComFlag} com flag · {resumo.flags}{' '}
          flags · {resumo.comunicacoes} comunicações geradas.
        </div>
      )}
      {erro && (
        <div className="mt-3 rounded-sm px-3 py-2 text-xs" style={{ background: 'var(--risk-soft)', color: 'var(--risk)' }}>
          {erro}
        </div>
      )}
    </div>
  );
}

export function AdminClienteDetalhe() {
  const [, params] = useRoute('/admin/clientes/:orgId');
  const orgId = params ? decodeURIComponent(params.orgId) : '';
  const { data, loading, error, reload } = useAsync(
    () => trpc.admin.cliente.query({ orgId }),
    [orgId],
  );
  const [aviso, setAviso] = useState<string | null>(null);

  function placeholder(acao: string) {
    setAviso(`"${acao}" ainda não executa lógica de agente nesta etapa. Vem na fase seguinte.`);
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-2">
        <Link href="/admin/intervencao" className="text-sm text-ink-3 hover:text-ink">
          ← Intervenção
        </Link>
      </div>
      <PageTitle titulo="Intervenção" />

      {loading && <Loading />}
      {error && <ErrorBox msg={error} onRetry={reload} />}
      {data === null && !loading && !error && (
        <ErrorBox msg="Cliente não encontrado." />
      )}

      {data && (
        <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
          {/* Coluna esquerda: identidade + diagnóstico */}
          <div className="space-y-5">
            <div className="card p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-lg font-semibold text-ink">{data.incorporadora.nome}</div>
                  <div className="mt-1 text-sm text-ink-3">
                    Plano {PLANO_LABEL[data.incorporadora.plano]} · {formatInt(data.contratosTotal)} contratos ·
                    assinatura {ASSINATURA_LABEL[data.incorporadora.statusAssinatura]}
                  </div>
                </div>
                {(data.incorporadora.statusAssinatura === 'inadimplente' ||
                  data.agentes.some((a) => a.status === 'falha')) && (
                  <span
                    className="rounded px-2 py-1 text-[11px] font-semibold uppercase tracking-wider"
                    style={{ background: 'var(--risk-soft)', color: 'var(--risk)' }}
                  >
                    Em risco
                  </span>
                )}
              </div>
            </div>

            {/* Diagnóstico do agente (quando há falha com detalhe) */}
            {data.agentes
              .filter((a) => a.status === 'falha' && a.detalhe)
              .map((a) => (
                <div key={a.tipo} className="card p-5">
                  <div className="text-[11px] uppercase tracking-wider text-ink-4">Diagnóstico do agente</div>
                  <div
                    className="mt-3 flex gap-3 rounded p-4 text-sm text-ink-2"
                    style={{ background: 'var(--risk-soft)', border: '1px solid var(--risk)' }}
                  >
                    <span
                      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded text-[10px] font-bold"
                      style={{ background: 'var(--risk)', color: '#fff' }}
                    >
                      IA
                    </span>
                    <span>{a.detalhe}</span>
                  </div>
                </div>
              ))}

            {/* Status dos agentes */}
            <div className="card p-5">
              <div className="text-[11px] uppercase tracking-wider text-ink-4">Status dos agentes</div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {data.agentes.map((a) => (
                  <div key={a.tipo} className="rounded-sm border border-line p-3">
                    <div className="text-xs text-ink-3">{TIPO_LABEL[a.tipo] ?? a.tipo}</div>
                    <div className="mt-2">
                      <StatusPill label={AGENTE_STATUS_LABEL[a.status]!} color={AGENTE_STATUS_COLOR[a.status]!} />
                    </div>
                  </div>
                ))}
                {data.agentes.length === 0 && (
                  <div className="col-span-3 text-sm text-ink-4">Sem agentes ativos para este cliente.</div>
                )}
              </div>
            </div>

            {/* Resumo da carteira */}
            <div className="card grid grid-cols-3 gap-4 p-5 text-center">
              <div>
                <div className="num text-2xl font-semibold text-ink">{formatInt(data.contratosTotal)}</div>
                <div className="mt-1 text-xs text-ink-3">contratos</div>
              </div>
              <div>
                <div className="num text-2xl font-semibold text-ink">{formatInt(data.compradoresTotal)}</div>
                <div className="mt-1 text-xs text-ink-3">compradores</div>
              </div>
              <div>
                <div className="num text-2xl font-semibold text-ink">{formatInt(data.parcelasTotal)}</div>
                <div className="mt-1 text-xs text-ink-3">parcelas</div>
              </div>
            </div>
          </div>

          {/* Coluna direita: ações + histórico */}
          <div className="space-y-5">
            <div className="card p-5">
              <div className="text-[11px] uppercase tracking-wider text-ink-4">Ações de suporte</div>
              <div className="mt-3 space-y-2">
                <AcaoSuporte label="Abrir workspace do cliente" onClick={() => placeholder('Abrir workspace do cliente')} />
                <AcaoSuporte label="Revisar comunicações sensíveis" badge={2} onClick={() => placeholder('Revisar comunicações sensíveis')} />
                <AcaoSuporte label="Pausar envios automáticos" onClick={() => placeholder('Pausar envios automáticos')} />
              </div>
              {aviso && (
                <div className="mt-3 rounded-sm px-3 py-2 text-xs" style={{ background: 'var(--surface-2)', color: 'var(--ink-3)' }}>
                  {aviso}
                </div>
              )}
            </div>

            <ModoDemonstracao orgId={orgId} override={data.demonstracaoOverride} onChangeOverride={reload} />

            <div className="card p-5">
              <div className="text-[11px] uppercase tracking-wider text-ink-4">Histórico</div>
              <ul className="mt-3 space-y-3">
                {data.historico.length === 0 && <li className="text-sm text-ink-4">Sem eventos registrados.</li>}
                {data.historico.map((h, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="num shrink-0 text-ink-4">
                      {new Date(h.criadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="text-ink-2">{h.titulo}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
